import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { Table as ShadTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { Input } from '@components/ui/input'
import { Button } from '@components/ui/button'
import { Search, ArrowUpDown } from 'lucide-react'
import { Card, CardContent } from '@components/ui/card'

const Table = ({ header, data, revealHiddenKey, actions, isLoading }) => {
  const { t } = useTranslation('common')
  const [search, setSearch] = useState('')
  const [actualData, setActualData] = useState([])
  const [sortConfig, setSortConfig] = useState({})

  useEffect(() => {
    setActualData(Array.isArray(data) ? data : [])
  }, [data])

  const processedData = useMemo(() => {
    let filteredData = [...actualData]
    if (search) {
      filteredData = filteredData.filter((item) => item.name?.toLowerCase().includes(search.toLowerCase()))
    }
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const valueA = a[sortConfig.key] || ''
        const valueB = b[sortConfig.key] || ''
        return sortConfig.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      })
    }
    return filteredData
  }, [actualData, search, sortConfig])

  const handleSort = (item) => {
    if (!item.sortable) return
    setSortConfig((prev) => ({
      key: item.key,
      direction: prev.key === item.key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const renderSkeletonRow = (index) => (
    <TableRow key={`skeleton-${index}`}>
      {header.map((_, j) => (
        <TableCell key={`skeleton-${index}-${j}`} className="px-4 py-2">
          <div className="w-3/4 h-4 rounded animate-pulse bg-muted" />
        </TableCell>
      ))}
      {actions && (
        <TableCell className="px-4 py-2 text-right">
          <div className="ml-auto w-20 h-8 rounded animate-pulse bg-muted" />
        </TableCell>
      )}
    </TableRow>
  )

  const renderDesktopTable = () => (
    <ShadTable className="table-fixed w-full">
      <TableHeader className="sticky top-0 bg-muted/50">
        <TableRow>
          {header.map((item, i) => (
            <TableHead key={`head-${i}`} className="px-4 py-3 font-semibold text-gray-700">
              <Button
                variant="ghost"
                onClick={() => handleSort(item)}
                className="flex gap-1 justify-start items-center px-1 w-full"
                disabled={!item.sortable}
              >
                {t(item.head)}
                {item.sortable && (
                  <ArrowUpDown
                    className={`w-4 h-4 ml-1 transition-transform ${
                      sortConfig.key === item.key && sortConfig.direction === 'desc' ? 'rotate-180' : ''
                    }`}
                  />
                )}
              </Button>
            </TableHead>
          ))}
          {actions && <TableHead className="px-4 py-3 font-semibold text-right text-gray-700">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array(5)
            .fill(0)
            .map((_, index) => renderSkeletonRow(index))
        ) : processedData.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={header.length + (actions ? 1 : 0)}
              className="px-4 py-6 text-center text-muted-foreground"
            >
              {t('common.no_data')}
            </TableCell>
          </TableRow>
        ) : (
          processedData.map((item, i) => (
            <TableRow key={item.token || i} className="hover:bg-gray-50">
              {header.map((column, j) => (
                <TableCell key={`cell-${i}-${j}`} className="px-4 py-3 align-middle">
                  {column.hidden && item.hidden ? (
                    <div className="inline-block relative">
                      <span className="blur-sm select-none">{item[column.key]}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-1/2 left-1/2 z-10 whitespace-nowrap transform -translate-x-1/2 -translate-y-1/2"
                        onClick={() => revealHiddenKey(i)}
                      >
                        {t('keys.reveal')}
                      </Button>
                    </div>
                  ) : (
                    <span className="block max-w-[300px] truncate" title={item[column.key]}>
                      {item[column.key]}
                    </span>
                  )}
                </TableCell>
              ))}
              {actions && (
                <TableCell className="px-4 py-3 text-right">
                  <div className="flex justify-end">{actions(item)}</div>
                </TableCell>
              )}
            </TableRow>
          ))
        )}
      </TableBody>
    </ShadTable>
  )

  const renderMobileTable = () => (
    <div className="space-y-4 md:hidden">
      {isLoading ? (
        Array(3)
          .fill(0)
          .map((_, i) => (
            <Card key={`skeleton-mob-${i}`}>
              <CardContent className="p-4 space-y-3">
                {header.map((_, j) => (
                  <div key={`skeleton-mob-${i}-${j}`} className="flex justify-between items-center">
                    <div className="w-20 h-4 rounded animate-pulse bg-muted" />
                    <div className="w-32 h-4 rounded animate-pulse bg-muted" />
                  </div>
                ))}
                {actions && (
                  <div className="flex justify-end pt-2 border-t">
                    <div className="w-20 h-8 rounded animate-pulse bg-muted" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))
      ) : processedData.length === 0 ? (
        <Card>
          <CardContent className="p-4 py-6 text-center text-muted-foreground">{t('common.no_data')}</CardContent>
        </Card>
      ) : (
        processedData.map((item, i) => (
          <Card key={item.token || i}>
            <CardContent className="p-4 space-y-4">
              {header.map((column, j) => (
                <div key={`mob-${i}-${j}`} className="flex flex-col space-y-1">
                  <span className="text-sm font-medium text-muted-foreground">{t(column.head)}</span>
                  <span className="font-medium break-words">
                    {column.hidden && item.hidden ? (
                      <div className="inline-block relative">
                        <span className="blur-sm select-none">{item[column.key]}</span>
                        <Button
                          variant="outline"
                          className="absolute top-1/2 left-1/2 z-10 whitespace-nowrap transform -translate-x-1/2 -translate-y-1/2"
                          size="sm"
                          onClick={() => revealHiddenKey(i)}
                        >
                          {t('keys.reveal')}
                        </Button>
                      </div>
                    ) : (
                      item[column.key]
                    )}
                  </span>
                </div>
              ))}
              {actions && <div className="flex justify-end pt-2 border-t">{actions(item)}</div>}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 w-4 h-4 transform -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search"
          className="pl-10 border-gray-200 transition-colors focus:border-blue-500"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="hidden overflow-x-auto rounded-md border md:block">{renderDesktopTable()}</div>
      {renderMobileTable()}
    </div>
  )
}

export default Table
